type DeferredPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export type PwaPlatform =
  | 'installable'
  | 'ios-safari'
  | 'ios-other'
  | 'samsung-internet'
  | 'android-other'
  | 'firefox-desktop'
  | 'desktop-other'
  | 'unknown';

let deferredPrompt: DeferredPrompt | null = null;
let installed = false;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as DeferredPrompt;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    emit();
  });
}

export function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true
    || installed
  );
}

export function detectPlatform(): PwaPlatform {
  if (typeof window === 'undefined') return 'unknown';
  if (deferredPrompt) return 'installable';

  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  if (isIOS) {
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(ua);
    return isSafari ? 'ios-safari' : 'ios-other';
  }
  if (/SamsungBrowser/i.test(ua)) return 'samsung-internet';
  if (/Android/.test(ua)) return 'android-other';
  if (/Firefox/.test(ua)) return 'firefox-desktop';
  return 'desktop-other';
}

export async function triggerInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  try {
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    emit();
    return choice.outcome;
  } catch {
    return 'unavailable';
  }
}
